import {Injectable} from "@angular/core";
import {
  FirebaseAuthentication,
  SignInResult,
} from "@capacitor-firebase/authentication";
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import {from, map, Observable, switchMap, throwError} from "rxjs";
import {FirebaseAppService} from "src/app/services/firebase-app.service";
import {PlatformService} from "src/app/services/platform.service";
import {SocialLoginProvider} from "../models/User";

@Injectable({
  providedIn: "root",
})
export class SocialAuthService {
  constructor(
    private readonly firebaseAppService: FirebaseAppService,
    private readonly platformService: PlatformService
  ) {}

  public signIn(provider: SocialLoginProvider): Observable<string> {
    if (this.platformService.isNativePlatform()) {
      return this.signInNative(provider);
    }

    const firebaseApp = this.firebaseAppService.getApp("SocialAuthService");

    if (!firebaseApp) {
      return throwError(() => new Error("FIREBASE_CONFIG_MISSING"));
    }

    const auth = getAuth(firebaseApp);
    return from(signInWithPopup(auth, this.buildProvider(provider))).pipe(
      switchMap((credential) => from(credential.user.getIdToken(true)))
    );
  }

  private signInNative(provider: SocialLoginProvider): Observable<string> {
    const signInPromise =
      provider === "google"
        ? this.signInWithGoogle()
        : FirebaseAuthentication.signInWithApple();

    return from(signInPromise).pipe(
      switchMap(() =>
        from(FirebaseAuthentication.getIdToken({forceRefresh: true}))
      ),
      map(({token}) => token)
    );
  }

  private async signInWithGoogle(): Promise<SignInResult> {
    try {
      return await FirebaseAuthentication.signInWithGoogle();
    } catch (error) {
      if (
        PlatformService.getPlatform() !== "android" ||
        !(error instanceof Error) ||
        !/^No credentials available\.?$/.test(error.message)
      ) {
        throw error;
      }

      // Let users select or add an account when the credential sheet has none available.
      return FirebaseAuthentication.signInWithGoogle({
        useCredentialManager: false,
      });
    }
  }

  private buildProvider(
    provider: SocialLoginProvider
  ): GoogleAuthProvider | OAuthProvider {
    if (provider === "google") {
      const googleProvider = new GoogleAuthProvider();
      googleProvider.addScope("email");
      googleProvider.addScope("profile");
      return googleProvider;
    }

    return this.buildAppleProvider();
  }

  private buildAppleProvider(): OAuthProvider {
    const appleProvider = new OAuthProvider("apple.com");
    appleProvider.addScope("email");
    appleProvider.addScope("name");
    return appleProvider;
  }
}
